import { DeleteOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { DeletedFilled } from 'assets/images/svg';
import { rolesConfig } from 'config/rolesConfig';
import { formatDisplayName } from 'library/helpers/utility';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectPeopleWithAdminRoles } from 'reduxStore/people/selectors';
import { roleActions } from 'reduxStore/roles/slice';
import { Button, Divider, Flex, Modal, Select, Typography } from 'zui/Antd';
import { ConditionalTooltipButton } from 'zui/Custom';

const { Title, Text } = Typography;

const DeleteSiteAdminModal = ({ children, personIDs = [], open, setOpen }) => {
	const {
		token: { paddingLG, fontSizeSM, marginLG, marginXS, paddingMD }
	} = theme.useToken();

	const dispatch = useDispatch();
	const [internalVisible, setInternalVisible] = useState(false);
	const visible = open !== undefined ? open : internalVisible;
	const setVisible = setOpen !== undefined ? setOpen : setInternalVisible;

	const people = useSelector(selectPeopleWithAdminRoles);
	const { loadingRevokeRole: loading } = useSelector((state) => state.Roles);

	// ✅ Select personIDs from props
	const filteredPeople = useMemo(() => people.filter((p) => personIDs.includes(p.personID)), [people, personIDs]);

	// ✅ All sites of the selected people
	const allSites = useMemo(() => {
		return filteredPeople.flatMap((person) => person.admin.sites || []);
	}, [filteredPeople]);

	// ✅ By default, select the first site
	const [selectedSiteID, setSelectedSiteID] = useState(allSites[0]?.siteID || null);

	// If change personIDs, reset selectedSiteID
	useEffect(() => {
		if (allSites.length > 0) {
			setSelectedSiteID(allSites[0].siteID);
		}
	}, [allSites]);

	const selectedNames = useMemo(
		() => filteredPeople.map((person) => formatDisplayName(person)?.displayName || `#${person.personID}`),
		[filteredPeople]
	);

	const generateDeleteTitle = () =>
		personIDs.length > 1
			? `Are you sure you want to revoke these ${personIDs.length} Site Admins?`
			: 'Are you sure you want to revoke this Site Admin?';

	const generateDeleteButtonText = () => 'Revoke Admin';

	const handleCancel = () => {
		setVisible(false);
		setSelectedSiteID(null);
	};

	const handleDelete = () => {
		for (const personID of personIDs) {
			dispatch(
				roleActions.revokeRole({
					personID,
					permissionID: rolesConfig.SITE_ADMIN,
					entityID: selectedSiteID
				})
			);
		}

		handleCancel();
	};

	return (
		<Fragment>
			<ConditionalTooltipButton
				buttonText={generateDeleteButtonText()}
				condition={false}
				disabledCondition={false}
				trueTitle={"You don't have permission"}
				falseTitle={'Remove Site Admin'}
				onClick={() => setVisible(true)}
				danger
				buttonType='primary'
				icon={<DeleteOutlined />}
			>
				{children}
			</ConditionalTooltipButton>

			<Modal
				open={visible}
				onCancel={handleCancel}
				closeIcon={false}
				centered
				destroyOnClose
				footer={[
					<Flex align='center' justify='center' style={{ marginTop: marginLG }} gap={12} key='footer'>
						<Button onClick={handleCancel} type='default' disabled={loading} block>
							Cancel
						</Button>
						<Button
							onClick={handleDelete}
							loading={loading}
							disabled={loading || !selectedSiteID}
							type='primary'
							danger
							block
						>
							{generateDeleteButtonText()}
						</Button>
					</Flex>
				]}
				width={350}
				styles={{ content: { padding: paddingMD } }}
			>
				<Flex vertical align='center' justify='center' gap={12} style={{ padding: `0 ${paddingLG}px` }}>
					<DeletedFilled />
					<Flex vertical gap={marginXS}>
						<Title level={5} style={{ margin: 0, lineHeight: '24px', textAlign: 'center' }}>
							{generateDeleteTitle()}
						</Title>
						<Text style={{ fontSize: fontSizeSM, lineHeight: '24px', textAlign: 'center' }}>
							This action is permanent and cannot be undone.
						</Text>

						<Flex vertical gap={marginXS} style={{ marginBottom: marginXS }}>
							<Divider style={{ marginBottom: 0 }}>
								<Text style={{ fontSize: fontSizeSM, fontWeight: 200 }}>Selected Site Admins</Text>
							</Divider>
							<Text style={{ fontSize: fontSizeSM, textAlign: 'center' }}>
								{selectedNames.join(', ')}
							</Text>

							<Divider style={{ margin: '12px 0' }}>
								<Text style={{ fontSize: fontSizeSM, fontWeight: 200 }}>Site</Text>
							</Divider>

							<Select
								placeholder='Select a Site to revoke'
								options={allSites.map((site) => ({
									label: site.displayName,
									value: site.siteID
								}))}
								value={selectedSiteID}
								onChange={(value) => setSelectedSiteID(value)}
								style={{ width: '100%' }}
								allowClear
							/>
						</Flex>
					</Flex>
				</Flex>
			</Modal>
		</Fragment>
	);
};

export default DeleteSiteAdminModal;
